import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, ImageOverlay, useMap } from 'react-leaflet';
import { CRS, LatLngTuple } from 'leaflet';

import { Device, FloorPlan } from 'api/InteractiveMap/types';
import HeatmapLayer from 'pages/InteractiveMap/components/HeatmapLayer';
import { useGetHeatMapSBSData } from 'pages/InteractiveMap/hooks/useGetHeatMapSBSData';
import { useGetDevicesOnMap } from 'hooks/InteractiveMap/useGetDevicesOnMap';
import DeviceMarker from 'pages/InteractiveMap/customMarker/DeviceMarker';
import useGetHeatMapTrapData from 'pages/InteractiveMap/hooks/useGetHeatMapTrapData';

interface PlanHeatMapProps {
  plan: FloorPlan;
  startDate: string;
  endDate: string;
}

function FitBounds({ bounds }: { bounds: [[number, number], [number, number]] }) {
  const map = useMap();
  useEffect(() => {
    map.fitBounds(bounds, { animate: false, padding: [0, 0] });
  }, [map, bounds]);
  return null;
}

const PlanHeatMap: React.FC<PlanHeatMapProps> = ({ plan, startDate, endDate }) => {
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });
  const { data: devices } = useGetDevicesOnMap(plan.slug);
  const markerRefs = useRef<Record<string, any>>({});

  useEffect(() => {
    function updateSize() {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    }
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    if (!plan) return;
    const img = new Image();
    img.src = plan.floor_plan;
    img.onload = () => {
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
  }, [plan]);

  const { data: heatmapData } = useGetHeatMapTrapData(plan.slug, startDate, endDate, 'mct');
  const { data: heatmapSBSData } = useGetHeatMapSBSData(plan?.slug || '', startDate, endDate);
  const { data: heatmapSRSTData } = useGetHeatMapTrapData(plan.slug, startDate, endDate, 'srst');
  const { data: heatmapSMSTData } = useGetHeatMapTrapData(plan.slug, startDate, endDate, 'smst');

  if (!imageDimensions || !containerSize.width) {
    return <div ref={containerRef} style={{ width: '100%', minHeight: 100 }} />;
  }

  // Це ширина листка А4 - паддінги і марджини
  const mapWidth = 700;
  const mapHeight = Math.round(mapWidth * (imageDimensions.height / imageDimensions.width));

  const imageBounds: [[number, number], [number, number]] = [
    [0, 0],
    [imageDimensions.height, imageDimensions.width],
  ];

  return (
    <>
      <h2>{plan.title}</h2>
      <div style={{ width: `${mapWidth}px`, margin: '0 auto' }}>
        <MapContainer
          crs={CRS.Simple}
          style={{ width: `${mapWidth}px`, height: `${mapHeight}px`, margin: '0 auto' }}
          doubleClickZoom={false}
          scrollWheelZoom
          zoomSnap={0.2}
          maxZoom={-1}
          minZoom={-1}
          dragging={false}
          touchZoom={false}
          boxZoom={false}
          keyboard={false}
          attributionControl
          zoomControl={false}
          zoom={mapWidth < 700 ? 0 : -1}
        >
          <FitBounds bounds={imageBounds} />
          <ImageOverlay url={plan.floor_plan} bounds={imageBounds} />
          {[
            devices?.mct as Device[] | undefined,
            devices?.srbm as Device[] | undefined,
            devices?.smst as Device[] | undefined,
            devices?.srst as Device[] | undefined,
          ]
            .flat()
            .map(device => {
              if (!device) return null;
              const position: LatLngTuple = [device.x, device.y];
              return (
                <DeviceMarker
                  key={device.slug}
                  device={device}
                  position={position}
                  deviceType={
                    device.model.startsWith('srbm')
                      ? 'srbm'
                      : device.model.startsWith('smst')
                        ? 'smst'
                        : device.model.startsWith('srst')
                          ? 'srst'
                          : 'mct'
                  }
                  markerRef={el => (markerRefs.current[device.slug] = el)}
                />
              );
            })}
          {heatmapData && (
            <HeatmapLayer
              points={heatmapData.map((point: { x: number; y: number; count: number }) => [
                point.x,
                point.y,
                point.count,
              ])}
              deviceType="mct"
            />
          )}
          {heatmapSRSTData && (
            <HeatmapLayer
              points={heatmapSRSTData.map((point: { x: number; y: number; count: number }) => [
                point.x,
                point.y,
                point.count,
              ])}
              deviceType="srst"
            />
          )}
          {heatmapSMSTData && (
            <HeatmapLayer
              points={heatmapSMSTData.map((point: { x: number; y: number; count: number }) => [
                point.x,
                point.y,
                point.count,
              ])}
              deviceType="smst"
            />
          )}
          {heatmapSBSData && (
            <HeatmapLayer points={heatmapSBSData.map(point => [point[0], point[1], point[2]])} deviceType="srbm" />
          )}
        </MapContainer>
      </div>
    </>
  );
};

export default PlanHeatMap;
